import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { catchError, of } from 'rxjs';
import { CostAnalysisStateService } from '../../cost-analysis/services/cost-analysis-state.service';
import { CostReportsCacheService } from '../../cost-analysis/services/cost-reports-cache.service';

@Component({
  selector: 'app-pools-page',
  templateUrl: './pools-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoolsPageComponent implements OnInit {
  private readonly state = inject(CostAnalysisStateService);
  private readonly costReportsCache = inject(CostReportsCacheService);

  readonly yearControl = new FormControl<number>(this.state.selectedYear(), [
    Validators.required,
    Validators.min(2020),
    Validators.max(2099),
  ]);
  readonly monthControl = new FormControl<number>(this.state.selectedMonth(), [Validators.required, Validators.min(1), Validators.max(12)]);
  readonly monthOptions: { label: string; key: string; value: number }[] = [
    { label: 'January', key: 'Month_Jan', value: 1 },
    { label: 'February', key: 'Month_Feb', value: 2 },
    { label: 'March', key: 'Month_Mar', value: 3 },
    { label: 'April', key: 'Month_Apr', value: 4 },
    { label: 'May', key: 'Month_May', value: 5 },
    { label: 'June', key: 'Month_Jun', value: 6 },
    { label: 'July', key: 'Month_Jul', value: 7 },
    { label: 'August', key: 'Month_Aug', value: 8 },
    { label: 'September', key: 'Month_Sep', value: 9 },
    { label: 'October', key: 'Month_Oct', value: 10 },
    { label: 'November', key: 'Month_Nov', value: 11 },
    { label: 'December', key: 'Month_Dec', value: 12 },
  ];

  private readonly _currentYear = signal<number>(this.yearControl.value ?? new Date().getFullYear());
  private readonly _currentMonth = signal<number>(this.monthControl.value ?? new Date().getMonth() + 1);
  readonly currentYear = this._currentYear.asReadonly();
  readonly currentMonth = this._currentMonth.asReadonly();

  readonly excludedCounts = signal<{ greige: number; yarn: number; unknown: number }>({ greige: 0, yarn: 0, unknown: 0 });
  readonly excludedTotal = computed(() => {
    const c = this.excludedCounts();
    return c.greige + c.yarn + c.unknown;
  });

  ngOnInit(): void {
    this.yearControl.valueChanges.subscribe((value) => {
      if (value === null || value === undefined || this.yearControl.invalid) return;
      this._currentYear.set(value);
      this.state.setPeriod(value, this._currentMonth());
      this.loadExcludedCounts();
    });
    this.monthControl.valueChanges.subscribe((value) => {
      if (value === null || value === undefined) return;
      this._currentMonth.set(value);
      this.state.setPeriod(this._currentYear(), value);
      this.loadExcludedCounts();
    });
    this.loadExcludedCounts();
  }

  onCalculated(): void {
    this.costReportsCache.invalidatePeriod(this._currentYear(), this._currentMonth());
    this.loadExcludedCounts();
  }

  private loadExcludedCounts(): void {
    const year = this._currentYear();
    const month = this._currentMonth();
    if (!year || !month) {
      this.excludedCounts.set({ greige: 0, yarn: 0, unknown: 0 });
      return;
    }
    this.costReportsCache
      .getPeriodSummary(year, month, this.state.costSourceFilter())
      .pipe(catchError(() => of({ success: false, data: null, errorMessage: null })))
      .subscribe((resp) => {
        this.excludedCounts.set(resp.success && resp.data ? resp.data.excludedCounts : { greige: 0, yarn: 0, unknown: 0 });
      });
  }
}
